import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { interviewAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { ArrowLeft, Play, Pause, SkipBack, SkipForward, User, Bot, Calendar, TrendingUp, RotateCcw } from 'lucide-react';
import { ReplayTurn } from './ReplayTurn';

const TYPE_LABEL = { realtime_voice: 'Voice Interview', structured_4phase: '4-Phase Visa Interview', text_chat: 'Text Interview' };
const STEP_MS = 2500;

const InterviewReplay = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [replay, setReplay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(0);
  const [playing, setPlaying] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    interviewAPI.getReplay(sessionId)
      .then(data => { setReplay(data); setCursor(data.turns?.length || 0); })
      .catch(() => toast({ title: 'Error', description: 'Failed to load replay', variant: 'destructive' }))
      .finally(() => setLoading(false));
  }, [sessionId]);

  const total = replay?.turns?.length || 0;

  useEffect(() => {
    if (!playing) return;
    if (cursor >= total) { setPlaying(false); return; }
    const t = setTimeout(() => setCursor(c => c + 1), STEP_MS);
    return () => clearTimeout(t);
  }, [playing, cursor, total]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }, [cursor]);

  const restart = () => { setCursor(0); setPlaying(true); };

  if (loading) {
    return <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center text-gray-700 text-xl">Loading replay...</div>;
  }
  if (!replay) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center">
        <Button onClick={() => navigate('/interview/history')}><ArrowLeft className="h-4 w-4 mr-2" />Back to history</Button>
      </div>
    );
  }

  const shown = replay.turns.slice(0, cursor);
  const scoreColor = replay.score >= 80 ? 'text-emerald-600' : replay.score >= 60 ? 'text-amber-600' : 'text-rose-600';

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 py-8" data-testid="interview-replay-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button variant="ghost" onClick={() => navigate('/interview/history')} className="mb-4" data-testid="replay-back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" />Back to history
        </Button>

        <Card className="mb-6 border-2 border-emerald-200 bg-white/80">
          <CardContent className="p-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">{TYPE_LABEL[replay.type] || replay.type}</Badge>
                {replay.visa_type && <Badge variant="outline">{replay.visa_type}</Badge>}
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Interview Replay</h1>
              <p className="text-sm text-gray-500 flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {replay.created_at ? new Date(replay.created_at).toLocaleString() : '—'} · {replay.question_count} questions
              </p>
            </div>
            {typeof replay.score === 'number' && (
              <div className="text-right" data-testid="replay-score">
                <p className="text-xs text-gray-500 flex items-center justify-end gap-1"><TrendingUp className="h-3 w-3" />Score</p>
                <p className={`text-4xl font-bold ${scoreColor}`}>{replay.score}<span className="text-lg text-gray-400">/100</span></p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Player controls */}
        <div className="sticky top-2 z-10 mb-4 flex items-center gap-2 p-3 rounded-xl bg-white/90 backdrop-blur border border-emerald-200 shadow-sm" data-testid="replay-controls">
          <Button size="sm" variant="outline" onClick={() => setCursor(c => Math.max(0, c - 1))} disabled={cursor === 0} data-testid="replay-prev-btn"><SkipBack className="h-4 w-4" /></Button>
          <Button size="sm" onClick={() => (cursor >= total ? restart() : setPlaying(p => !p))} className="bg-gradient-to-r from-emerald-500 to-teal-500" data-testid="replay-play-btn">
            {cursor >= total ? <RotateCcw className="h-4 w-4" /> : playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setCursor(c => Math.min(total, c + 1))} disabled={cursor >= total} data-testid="replay-next-btn"><SkipForward className="h-4 w-4" /></Button>
          <div className="flex-1 h-2 rounded-full bg-emerald-100 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-[width] duration-300" style={{ width: `${total ? (cursor / total) * 100 : 0}%` }} />
          </div>
          <span className="text-xs text-gray-600 w-16 text-right" data-testid="replay-progress">{cursor}/{total}</span>
        </div>

        <div className="space-y-4" data-testid="replay-transcript">
          {shown.length === 0 && <p className="text-center text-gray-500 py-8">Press play to start the replay</p>}
          {shown.map((t, i) => <ReplayTurn key={i} turn={t} index={i} />)}
          <div ref={endRef} />
        </div>

        {cursor >= total && replay.feedback?.summary && (
          <Card className="mt-8 border-2 border-teal-200 bg-white/80" data-testid="replay-summary">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-2 flex items-center gap-2"><Bot className="h-4 w-4 text-emerald-600" />Session Summary</h3>
              <p className="text-gray-700">{replay.feedback.summary}</p>
              {replay.feedback.recommendation && <Badge className="mt-3 bg-teal-100 text-teal-800">{replay.feedback.recommendation}</Badge>}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default InterviewReplay;
